import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  OnInit,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  AfterViewInit,
  SimpleChanges,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { ChevronLeft, ChevronRight, Cross, Download, Plus, Rotate, Subtract, Trash } from '../icon/icon-map';
import { PhxGalleryImage } from './phx-gallery.type';
import { PhxImagePanZoomComponent } from '../image-pan-zoom/phx-image-pan-zoom.component';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { PhxBreakpointObserverService } from '../../services/phx-breakpoint-service';
import { Subscription } from 'rxjs';
import { PhxIconButtonSize } from '../icon-button/phx-icon-button.types';
import { PHX_SIZE } from '../../constants';

@Component({
  selector: 'phx-gallery',
  templateUrl: './phx-gallery.component.html',
  styleUrls: ['./phx-gallery.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'phx-gallery',
  },
})
export class PhxGalleryComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() public phxImages: PhxGalleryImage[] = [];
  @Input() public phxSelectedIndex: number = 0;
  @Input() public phxEditMode: boolean = true;
  @Input() public phxOpen: boolean = false;
  @Input() public phxOptimisticDelete: boolean = true;

  @Output() public phxImageNavigate = new EventEmitter<number>();
  @Output() public phxOpenChange = new EventEmitter<boolean>();
  @Output() public phxDeleteImage = new EventEmitter<number>();
  @Output() public phxDownloadImage = new EventEmitter<PhxGalleryImage>();
  @Output() public phxRotateImage = new EventEmitter<{rotatedImage: string, index: number}>();
  @Output() public phxImagesChange = new EventEmitter<PhxGalleryImage[]>();

  @ViewChildren('phxImagePanZoom', { read: PhxImagePanZoomComponent }) public imagePanZoom: QueryList<PhxImagePanZoomComponent>;

  public currentIndex: number = 0;
  public isAnimating: boolean = false;
  private rotatedImages: Map<number, string> = new Map();
  public buttonSize: PhxIconButtonSize = PHX_SIZE.SM;
  public zoomPercentage: string = '';

  public readonly ZoomOutIcon: string = Subtract;
  public readonly ZoomInIcon: string = Plus;
  public readonly CloseIcon: string = Cross;
  public readonly RotateIcon: string = Rotate;
  public readonly DeleteIcon: string = Trash;
  public readonly DownloadIcon: string = Download;
  public readonly ChevronLeftIcon: string = ChevronLeft;
  public readonly ChevronRightIcon: string = ChevronRight;
  
  private breakpointObserverSubscription: Subscription;
  private imagePanZoomSubscription: Subscription;
  private imagePanZoomComponents: PhxImagePanZoomComponent[] = [];
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private touchEndX: number = 0;
  private touchEndY: number = 0;
  private lastTapTime: number = 0;
  private isZoomedIn: boolean = false;
  private initialPinchDistance: number = 0;
  private isPinching: boolean = false;
  private hadMultiTouch: boolean = false;
  private readonly swipeThreshold: number = 50;
  private readonly doubleTapDelay: number = 300;
  private readonly pinchThreshold: number = 10;

  constructor(
    private cdr: ChangeDetectorRef,
    private _phxBreakpointObserver: PhxBreakpointObserverService
  ) { }

  public ngOnInit(): void {
    this.breakpointObserverSubscription = this._phxBreakpointObserver.smMobile.subscribe((isMobile) => {
      if(isMobile) {
        this.buttonSize = PHX_SIZE.LG;
      } else {
        this.buttonSize = PHX_SIZE.SM;
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxSelectedIndex, phxOpen } = changes;

    if (phxSelectedIndex) {
      this.currentIndex = phxSelectedIndex.currentValue;
    }
    
    if (phxOpen && phxOpen.currentValue) {
      this.currentIndex = this.phxSelectedIndex;
      this.rotatedImages.clear();
      this.updateZoomPercentage();
    }
  }

  public ngAfterViewInit(): void {
    this.imagePanZoomComponents = this.imagePanZoom.toArray();
    this.imagePanZoomSubscription = this.imagePanZoom.changes.subscribe(() => {
      this.imagePanZoomComponents = this.imagePanZoom.toArray();
    });
  }

  public ngOnDestroy(): void {
    this.breakpointObserverSubscription.unsubscribe();
    this.imagePanZoomSubscription.unsubscribe();
  }

  public onNextImage(): void {
    if (this.currentIndex < this.phxImages.length - 1) {
      this.currentIndex++;
      this.updateZoomPercentage();
      this.phxImageNavigate.emit(this.currentIndex);
    }
  }

  public onPreviousImage(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateZoomPercentage();
      this.phxImageNavigate.emit(this.currentIndex);
    }
  }

  public onCloseClick(): void {
    this.phxOpen = false;
    this.phxOpenChange.emit(this.phxOpen);
  }

  public onDeleteClick(): void {
    const indexToDelete = this.currentIndex;

    // Emit delete event
    this.phxDeleteImage.emit(indexToDelete);

    // Only perform optimistic delete if flag is enabled
    if (this.phxOptimisticDelete) {
      this.phxImages.splice(indexToDelete, 1);
      this.phxImagesChange.emit(this.phxImages);

      if (this.phxImages.length === 0) {
        // Close gallery if no images left
        this.onCloseClick();
        return;
      }

      if (this.currentIndex >= this.phxImages.length) this.currentIndex = this.phxImages.length - 1;
      this.phxImageNavigate.emit(this.currentIndex);

      this.cdr.detectChanges();
    }
  }

  public onDownloadClick(): void {
    const imageData = this.phxImages[this.currentIndex];
    this.downloadImage(imageData);
    this.phxDownloadImage.emit(imageData);
  }

  public onRotateClick(): void {
    const imagePanZoomComponent = this.getCurrentImagePanZoomComponent();
    if (imagePanZoomComponent) {
      // Get the original MIME type from the current image
      const currentImage = this.phxImages[this.currentIndex];
      const originalMimeType = currentImage?.type;
      
      const rotatedImage = imagePanZoomComponent.rotateInDegrees(-90, originalMimeType);
      this.rotatedImages.set(this.currentIndex, rotatedImage); // Store rotated image temporarily (will reset on gallery reopen)
      this.phxRotateImage.emit({ rotatedImage: rotatedImage, index: this.currentIndex });
      this.updateZoomPercentage();
    }
  }

  public onZoomInClick(): void {
    const imagePanZoomComponent = this.getCurrentImagePanZoomComponent();
    if (imagePanZoomComponent) {
      imagePanZoomComponent.zoomIn();
      this.updateZoomPercentage();
    }
  }

  public onZoomOutClick(): void {
    const imagePanZoomComponent = this.getCurrentImagePanZoomComponent();
    if (imagePanZoomComponent) {
      imagePanZoomComponent.zoomOut();
      this.updateZoomPercentage();
    }
  }

  public getImageSrc(index: number): string {
    // Return rotated image if it exists, otherwise return original
    return this.rotatedImages.get(index) || this.phxImages[index]?.src;
  }

  public get imageTitle(): string {
    return this.phxImages[this.currentIndex]?.title;
  }

  public get imagesCount(): number {
    return this.phxImages.length;
  }

  public get activeImageIndex(): number {
    return this.currentIndex + 1; // 1-based index for display
  }

  public get imageCrossOrigin(): string | null {
    return this.phxEditMode ? 'Anonymous' : null;
  }

  public getSlideClasses(index: number): Record<string, boolean> {
    const isCurrent = this.isCurrentImage(index);
    const isPrev = this.isPrevImage(index);
    const isNext = this.isNextImage(index);

    return {
      'phx-gallery-slide-current': isCurrent,
      'phx-gallery-slide-next': isNext,
      'phx-gallery-slide-prev': isPrev,
    };
  }
   

  public updateZoomPercentage(): void {
    setTimeout(() => {
      this.zoomPercentage = this.getCurrentImagePanZoomComponent()?.percentageZoomText || '';
      this.cdr.markForCheck();
    }, 200);
  }

  private downloadImage(imageData: PhxGalleryImage): void {
    fetch(imageData.src, { cache: 'reload' })
      .then(response => {
        if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
        return response.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = imageData.title || 'image';
        a.click();

        URL.revokeObjectURL(blobUrl);
      })
      .catch(err => console.error('Download error:', err));
  }

  private isCurrentImage(index: number): boolean {
    return index === this.currentIndex;
  }

  private isNextImage(index: number): boolean {
    return index === this.currentIndex + 1;
  }

  private isPrevImage(index: number): boolean {
    return index === this.currentIndex - 1;
  }

  public shouldRenderImage(index: number): boolean {
    return this.isCurrentImage(index) || this.isNextImage(index) || this.isPrevImage(index);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (!this.phxOpen) {
      return;
    }

    switch (event.key) {
      case KeyboardKeys.ARROW_LEFT:
        event.preventDefault();
        this.onPreviousImage();
        break;
      case KeyboardKeys.ARROW_RIGHT:
        event.preventDefault();
        this.onNextImage();
        break;
      case KeyboardKeys.ESC:
        event.preventDefault();
        this.onCloseClick();
        break;
    }
  }

  public onMouseWheel(event: WheelEvent): void {
    if (!this.phxOpen) {
      return;
    }

    event.preventDefault();

    const imagePanZoomComponent = this.getCurrentImagePanZoomComponent();
    if (!imagePanZoomComponent) {
      return;
    }

    // Make zooming gradual based on scroll amount
    // deltaY is typically in the range of -100 to 100 for trackpad
    const zoomFactor = Math.abs(event.deltaY) / 100;
    const gradualZoom = Math.min(zoomFactor * 0.3, 0.3); // Cap at 0.3 for smooth zooming

    if (event.deltaY < 0) {
      imagePanZoomComponent.zoomIn(gradualZoom);
    } else if (event.deltaY > 0) {
      imagePanZoomComponent.zoomOut(gradualZoom);
    }
    this.updateZoomPercentage();
  }

  public onTouchStart(event: TouchEvent): void {
    if (!this.phxOpen || event.touches.length === 0) {
      return;
    }

    // Handle two-finger pinch
    if (event.touches.length === 2) {
      this.isPinching = true;
      this.hadMultiTouch = true;
      this.initialPinchDistance = this.getPinchDistance(event.touches[0], event.touches[1]);
      event.preventDefault();
      return;
    }

    // Handle single finger touch for swipe/tap
    this.isPinching = false;
    this.hadMultiTouch = false;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  public onTouchMove(event: TouchEvent): void {
    if (!this.phxOpen) {
      return;
    }

    // Track if multiple fingers are being used
    if (event.touches.length > 1) {
      this.hadMultiTouch = true;
    }

    if (!this.isPinching || event.touches.length !== 2) {
      return;
    }

    event.preventDefault();

    const currentPinchDistance = this.getPinchDistance(event.touches[0], event.touches[1]);
    const distanceDifference = currentPinchDistance - this.initialPinchDistance;

    // Check if the pinch distance change exceeds threshold
    if (Math.abs(distanceDifference) > this.pinchThreshold) {
      if (distanceDifference > 0) {
        // Pinch out - zoom in
        this.onZoomInClick();
      } else {
        // Pinch in - zoom out
        this.onZoomOutClick();
      }
      // Update initial distance to prevent continuous zooming
      this.initialPinchDistance = currentPinchDistance;
    }
  }

  public onTouchEnd(event: TouchEvent): void {
    if (!this.phxOpen) {
      return;
    }

    // Reset pinch state
    if (this.isPinching) {
      this.isPinching = false;
      this.initialPinchDistance = 0;
      return;
    }

    if (event.changedTouches.length === 0) {
      return;
    }

    this.touchEndX = event.changedTouches[0].clientX;
    this.touchEndY = event.changedTouches[0].clientY;

    // Only handle swipe if it was a single-finger gesture throughout
    if (!this.hadMultiTouch) {
      this.handleSwipeGesture();
      this.handleDoubleTapGesture();
    }

    // Reset multi-touch flag for next gesture
    this.hadMultiTouch = false;
  }

  private getPinchDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private handleSwipeGesture(): void {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;

    // Check if horizontal swipe is more significant than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.swipeThreshold) {
      if (deltaX > 0) {
        // Swipe right - go to previous image
        this.onPreviousImage();
      } else {
        // Swipe left - go to next image
        this.onNextImage();
      }
    }
  }

  private handleDoubleTapGesture(): void {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - this.lastTapTime;

    // Check if this is a double tap
    if (tapLength < this.doubleTapDelay && tapLength > 0) {
      // Toggle zoom on double tap
      if (this.isZoomedIn) {
        this.onZoomOutClick();
        this.isZoomedIn = false;
      } else {
        this.onZoomInClick();
        this.isZoomedIn = true;
      }
      this.lastTapTime = 0; // Reset to prevent triple tap
    } else {
      this.lastTapTime = currentTime;
    }
  }

  private getCurrentImagePanZoomComponent(): PhxImagePanZoomComponent | undefined {
    // Since we only render prev, current, and next images,
    // we need to find the component that corresponds to the current image
    const components = this.imagePanZoomComponents;

    // Calculate which rendered images we have
    const renderedIndexes: number[] = [];
    if (this.currentIndex > 0) renderedIndexes.push(this.currentIndex - 1); // prev
    renderedIndexes.push(this.currentIndex); // current
    if (this.currentIndex < this.phxImages.length - 1) renderedIndexes.push(this.currentIndex + 1); // next

    // Find the position of current index in rendered images
    const componentIndex = renderedIndexes.indexOf(this.currentIndex);

    return components[componentIndex];
  }

  public get hasNextImage(): boolean {
    return this.currentIndex < this.phxImages.length - 1;
  }

  public get hasPreviousImage(): boolean {
    return this.currentIndex > 0;
  }
}
